import { ref, watch } from 'vue';
import type { Ref } from 'vue';
import { KEYBOARD_KEY, getKeyDownHandler } from '@/constants/keyboard';
import { accDiv } from '@/utils/common';
import { isFunction } from '@/utils/is';

type pageChangeType = ({
  page,
  limit,
}: {
  page: number;
  limit: number;
}) => void;

interface TablePager {
  page?: number;
  limit?: number;
  pageSize?: number;
  totalCount?: number;
}

// 给vxe-table设置上下键切换高亮行，enter切换选中，pageUp(pageDown)切换分页
// 注意设置checkbox-config的reserve: true，并且设置row-config的isCurrent: true
// checkbox时使用getCheckboxReserveRecords获取已保留选中的行数据并合并当前页数据，并选择时机清除保留数据clearCheckboxReserve
const useTableKeyDown = ({
  tableRef, // 表格ref
  tableData, // 表格数据
  keyField = 'id', // row的唯一索引
  mode = 'checkbox', // 多选 | 单选
  loading,
  pageParams, // 分页信息，包括page、limit（pageSize）、totalCount
  pageChange, // 快捷键切换分页操作
}: {
  tableRef: Ref<any>;
  tableData: Ref<Recordable[]>;
  keyField?: any; // 行的唯一值
  mode?: 'checkbox' | 'radio';
  loading?: Ref<boolean>;
  pageParams?: TablePager;
  pageChange?: pageChangeType;
}) => {
  // 当前高亮行
  const currentRow = ref<Recordable>({});
  // 多选选中行信息
  const selectRows = ref<Recordable[]>([]);

  // 上下切换高亮行
  const navigateRows = (direction: 'up' | 'down') => {
    if (!tableRef.value) return;
    if (!tableData.value.length) return;
    const curIndex = currentRow.value
      ? tableData.value.findIndex(
          (row: Recordable) => row[keyField] === currentRow.value[keyField]
        )
      : -1;
    setCurrent(getNextRow(direction, curIndex) as Recordable);
  };
  // 循环列表获取“下一行”
  const getNextRow = (direction: 'up' | 'down', index: number) => {
    if (direction === 'down') {
      const nextIndex = index + 1;
      const nextOption = tableData.value.slice(nextIndex)[0];
      return nextOption || tableData.value[0];
    }
    const prevIndex = index - 1;
    if (prevIndex > -1 && prevIndex < tableData.value.length) {
      const prevOption = [...tableData.value.slice(0, index)].reverse()[0];
      if (prevOption) return prevOption;
    }
    return tableData.value[tableData.value.length - 1];
  };
  // 设置当前行
  const setCurrent = (row: Recordable = {}) => {
    changeCurrentRow({ row });
    if (!tableRef.value) return;
    if (!row[keyField]) return;
    tableRef.value.setCurrentRow(row);
    scrollToRow(row);
  };
  // const getCurrentRow = () => {
  //   if (!currentRow.value) return {};
  //   const curIndex = tableData.value.findIndex(
  //     (row: Recordable) => row[keyField] === currentRow.value[keyField]
  //   );
  //   return { item: tableData.value[curIndex], index: curIndex };
  // };
  // 切换当前行
  const changeCurrentRow = ({ row }: Recordable) => {
    currentRow.value = row;
  };

  // 上下切换分页跳转
  const navigatePager = (direction: 'up' | 'down') => {
    if (!isFunction(pageChange)) return;
    const {
      page = 0,
      totalCount = 0,
      limit: tempLimit = 0,
      pageSize,
    } = pageParams as TablePager;
    const limit = tempLimit ?? pageSize;
    const pagerCount = Math.ceil(accDiv(totalCount, limit) ?? 0);
    if (!pagerCount) return;
    if (direction === 'up' && page < 2) {
      // 第一页再往上就跳到最后一页
      if (pagerCount > 1) {
        pageChange({ page: pagerCount, limit });
      }
      return;
    }
    if (direction === 'down' && page >= pagerCount) {
      // 最后一页再往下就跳到第一页
      if (pagerCount > 1) {
        pageChange({ page: 1, limit });
      }
      return;
    }
    const newPage = direction === 'up' ? page - 1 : page + 1;
    pageChange({ page: newPage, limit });
  };

  // 处理enter事件（切换选中）
  const handleEnter = () => {
    if (!tableRef.value) return;
    if (!currentRow.value[keyField]) return;
    toggleCheckRow(currentRow.value);
    const rowIndex = tableData.value.findIndex(
      (item: Recordable) => item[keyField] === currentRow.value[keyField]
    );
    if (mode === 'radio') {
      tableRef.value.$emit('radioChange', {
        row: currentRow.value,
        rowIndex,
      });
      return;
    }
    const checked = tableRef.value.isCheckedByCheckboxRow(currentRow.value);
    const records = tableRef.value.getCheckboxRecords();
    const reserves = tableRef.value.getCheckboxReserveRecords();
    tableRef.value.$emit('checkboxChange', {
      checked,
      row: currentRow.value,
      records,
      reserves,
      rowIndex,
    });
  };

  // 切换选中状态
  const toggleCheckRow = (row: Recordable) => {
    if (!tableRef.value) return;
    if (!row[keyField]) return;
    if (mode === 'radio') {
      if (tableRef.value.isCheckedByRadioRow(row)) {
        // 取消单选
        tableRef.value.clearRadioRow();
        return;
      }
      setCheckRow(row); // 选中
      return;
    }
    if (mode === 'checkbox') {
      // 切换行多选
      tableRef.value.toggleCheckboxRow(row);
    }
  };

  // 设置行的选中状态
  const setCheckRow = (row: Recordable, checked?: boolean) => {
    if (!tableRef.value) return;
    if (!row[keyField]) return;
    if (mode === 'radio') {
      tableRef.value.setRadioRow(row);
      return;
    }
    tableRef.value.setCheckboxRow(row, checked);
  };

  // 滚动到指定行
  const scrollToRow = (row: Recordable) => {
    if (!tableRef.value) return;
    if (!row[keyField]) return;
    tableRef.value.scrollToRow(row);
  };

  // 合并多选选中行
  const handleMulSelectRow = (current: Recordable[] = []) => {
    if (!tableRef.value) return;
    // reserves获取已保留选中的行数据（不包含当前列表
    const reserves = tableRef.value.getCheckboxReserveRecords();
    selectRows.value = reserves.concat(current);
  };

  // 单选时设置为当前行并记录
  const handleRadioRow = ({ row }: any) => {
    if (!tableRef.value) return;
    if (!row[keyField]) return;
    changeCurrentRow({ row });
    selectRows.value = [row];
  };

  // 清除当前表格的选中行
  const clearCheckRow = () => {
    if (!tableRef.value) return;
    if (mode === 'radio') {
      tableRef.value.clearRadioRow();
      return;
    }
    tableRef.value.clearCheckboxRow();
  };

  // 清除选中数据和保留选中
  const clearSelectRow = () => {
    const $table = tableRef.value;
    if ($table) {
      if (mode === 'radio') {
        $table.clearRadioReserve();
      } else {
        $table.clearCheckboxReserve();
      }
      clearCheckRow();
    }
    selectRows.value = [];
  };

  watch(
    () => tableData.value,
    (newVal: Recordable[]) => {
      if (!tableRef.value) return;
      if (newVal.length) {
        const selectIds =
          selectRows.value?.map((row: Recordable) => row[keyField]) ?? [];
        newVal.forEach((row: Recordable) => {
          if (selectIds.includes(row[keyField])) {
            // 回显保留行
            setCheckRow(row, true);
          }
        });
      }
      // 取消当前高亮行
      setCurrent();
    }
  );
  // 生成handleKeyDown
  const handleKeyDown = getKeyDownHandler(
    new Map([
      [
        KEYBOARD_KEY.ENTER,
        (e: Event) => {
          // 选中
          if (!tableRef.value || loading?.value) return;
          e.preventDefault();
          handleEnter();
        },
      ],
      [
        KEYBOARD_KEY.ARROW_DOWN,
        (e: Event) => {
          // 切换到下一行高亮
          if (!tableRef.value || loading?.value) return;
          e.preventDefault();
          navigateRows('down');
        },
      ],
      [
        KEYBOARD_KEY.ARROW_UP,
        (e: Event) => {
          // 切换到上一行高亮
          if (!tableRef.value || loading?.value) return;
          e.preventDefault();
          navigateRows('up');
        },
      ],
      [
        KEYBOARD_KEY.PAGE_UP,
        (e: Event) => {
          // 上一页
          if (!tableRef.value || loading?.value) return;
          if (!pageParams || !isFunction(pageChange)) return;
          e.preventDefault();
          navigatePager('up');
        },
      ],
      [
        KEYBOARD_KEY.PAGE_DOWN,
        (e: Event) => {
          // 下一页
          if (!tableRef.value || loading?.value) return;
          if (!pageParams || !isFunction(pageChange)) return;
          e.preventDefault();
          navigatePager('down');
        },
      ],
    ])
  );

  return {
    handleKeyDown, // 用于设置@keydown，同时需要设置tabindex获取焦点
    changeCurrentRow, // 用于切换当前行
    selectRows, // 多选当前选中行信息
    handleMulSelectRow, // 多选选择行时记录
    handleRadioRow, // 单选选择行时记录
    clearSelectRow, // 清除已选行
  };
};

export default useTableKeyDown;
